import { graphqlQuery } from '@/lib/api/graphql';
import { mockNotesList } from '@/lib/demo-data/notes-list';
import { buildGraphQLSortVariable, type GraphQLCursorConnection } from '@/lib/graphql-pagination';
import { useApiConfig } from '@/providers/ApiConfigProvider';
import type { NoteEntityType, NoteListItem } from '@/types/note-list';
import { type CursorPaginationResult, useCursorPagination } from './useCursorPagination';
import { useModeAwareQuery } from './useModeAwareQuery';

// --- Params ---

export interface NotesListParams {
  search?: string;
  entityType?: string[];
  noteType?: string;
  dateFrom?: string;
  dateTo?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

// --- GraphQL types ---

interface NoteListGraphQLNode {
  noteId: number;
  entityType: string;
  entityId: number;
  entityReference: string | null;
  customerId: number | null;
  customerName: string | null;
  customerAccountNumber: string | null;
  noteType: string | null;
  content: string | null;
  isPrivate: boolean;
  isPinned: boolean;
  createdByUserId: number | null;
  createdByName: string | null;
  createdAt: string;
  updatedByName: string | null;
  updatedAt: string | null;
}

interface NoteListGraphQLResponse {
  notes: {
    noteList: GraphQLCursorConnection<NoteListGraphQLNode>;
  };
}

interface NoteCountGraphQLResponse {
  notes: {
    noteCount: number;
  };
}

// --- GraphQL query ---

const NOTE_LIST_QUERY = `
  query NoteList($first: Int, $after: String, $sort: [NoteListSortSortByInput!], $filter: NoteListFilterInput) {
    notes {
      noteList(first: $first, after: $after, sort: $sort, filter: $filter) {
        edges {
          node {
            noteId
            entityType
            entityId
            entityReference
            customerId
            customerName
            customerAccountNumber
            noteType
            content
            isPrivate
            isPinned
            createdByUserId
            createdByName
            createdAt
            updatedByName
            updatedAt
          }
        }
        pageInfo {
          endCursor
          hasNextPage
        }
      }
    }
  }
`;

const NOTE_COUNT_QUERY = `
  query NoteCount($filter: NoteListFilterInput) {
    notes {
      noteCount(filter: $filter)
    }
  }
`;

export const NOTES_PAGE_SIZE = 25;

const SYSTEM_AUTHOR = 'System';

const sortFieldMap: Record<string, string> = {
  entityType: 'EntityType',
  entityReference: 'EntityReference',
  customerName: 'CustomerName',
  customerAccountNumber: 'CustomerAccountNumber',
  noteType: 'NoteType',
  content: 'Content',
  createdByName: 'CreatedByName',
  createdAt: 'CreatedAt',
  updatedByName: 'UpdatedByName',
  updatedAt: 'UpdatedAt',
};

// Paging is page-number based, so ties on the sorted column have to break
// deterministically or rows can repeat or vanish between pages. Note ids are
// only unique within their own domain, hence the entity type as well.
const TIEBREAK_SORT_FIELDS = ['CreatedAt', 'EntityType', 'NoteId'];

function buildSortVariable(sortBy?: string, sortOrder?: 'asc' | 'desc') {
  const order = sortOrder === 'asc' ? 'Ascending' : 'Descending';
  const primary = buildGraphQLSortVariable(sortFieldMap, sortBy ?? 'createdAt', sortOrder ?? 'desc') ?? [];
  const tiebreakers = TIEBREAK_SORT_FIELDS.filter((field) => !primary.some((x) => x.field === field)).map((field) => ({
    field,
    order,
  }));

  return [...primary, ...tiebreakers];
}

// --- Mapping ---

function mapGraphQLNodeToNote(node: NoteListGraphQLNode): NoteListItem {
  return {
    noteId: node.noteId,
    entityType: node.entityType as NoteEntityType,
    entityId: node.entityId,
    entityReference: node.entityReference,
    customerId: node.customerId,
    customerName: node.customerName,
    customerAccountNumber: node.customerAccountNumber,
    noteType: node.noteType,
    content: node.content ?? '',
    isPrivate: node.isPrivate,
    isPinned: node.isPinned,
    createdByUserId: node.createdByUserId,
    createdByName: node.createdByName ?? SYSTEM_AUTHOR,
    createdAt: node.createdAt,
    updatedByName: node.updatedByName,
    updatedAt: node.updatedAt,
  };
}

// --- Filter / sort builders ---

export function buildNotesFilterVariable(params: NotesListParams): Record<string, unknown> | undefined {
  const filter: Record<string, unknown> = {};

  if (params.search) {
    filter.summary = params.search;
  }
  if (params.entityType && params.entityType.length > 0) {
    filter.entityType =
      params.entityType.length === 1 ? { eq: params.entityType[0] } : { containsValues: params.entityType };
  }
  if (params.noteType && params.noteType !== 'all') {
    filter.noteType = { eq: params.noteType };
  }
  const createdAtRange: Record<string, string> = {};
  if (params.dateFrom) {
    createdAtRange.ge = `${params.dateFrom}T00:00:00`;
  }
  if (params.dateTo) {
    createdAtRange.le = `${params.dateTo}T23:59:59.999`;
  }
  if (Object.keys(createdAtRange).length > 0) {
    filter.createdAt = createdAtRange;
  }

  return Object.keys(filter).length > 0 ? filter : undefined;
}

// --- Demo filtering ---

function matchesSearch(note: NoteListItem, search: string): boolean {
  const needle = search.toLowerCase();
  return [note.content, note.createdByName, note.customerName, note.customerAccountNumber, note.entityReference].some(
    (value) => (value ?? '').toLowerCase().includes(needle),
  );
}

function filterMockNotes(params: NotesListParams): NoteListItem[] {
  return mockNotesList.filter((note) => {
    if (params.search && !matchesSearch(note, params.search)) return false;
    if (params.entityType && params.entityType.length > 0 && !params.entityType.includes(note.entityType)) return false;
    if (params.noteType && params.noteType !== 'all' && note.noteType !== params.noteType) return false;
    if (params.dateFrom && note.createdAt < params.dateFrom) return false;
    if (params.dateTo && note.createdAt.split('T')[0] > params.dateTo) return false;
    return true;
  });
}

function sortMockNotes(notes: NoteListItem[], params: NotesListParams): NoteListItem[] {
  const sortBy = params.sortBy ?? 'createdAt';
  const order = params.sortOrder === 'asc' ? 1 : -1;

  return [...notes].sort((a, b) => {
    const left = String(a[sortBy as keyof NoteListItem] ?? '');
    const right = String(b[sortBy as keyof NoteListItem] ?? '');
    return order * left.localeCompare(right);
  });
}

/** Every demo note matching the screen's filters and sort, unpaged — used by the demo-mode export. */
export function getFilteredMockNotes(params: NotesListParams): NoteListItem[] {
  return sortMockNotes(filterMockNotes(params), params);
}

// --- Hook ---

/**
 * Cursor-paginated notes across all five note domains, served by the v_Notes_List union view.
 */
export function useNotesListGraphQL(params: NotesListParams): CursorPaginationResult<NoteListItem> {
  const { search, entityType, noteType, dateFrom, dateTo, sortBy, sortOrder } = params;

  return useCursorPagination<NoteListGraphQLNode, NoteListItem>(
    {
      pageSize: NOTES_PAGE_SIZE,
      buildMockData: () => sortMockNotes(filterMockNotes(params), params),
      executeQuery: async (apiConfig, afterCursor, signal) => {
        const variables: Record<string, unknown> = {
          first: NOTES_PAGE_SIZE,
          sort: buildSortVariable(sortBy, sortOrder),
          filter: buildNotesFilterVariable(params),
        };
        if (afterCursor) {
          variables.after = afterCursor;
        }

        const response = await graphqlQuery<NoteListGraphQLResponse>(apiConfig, NOTE_LIST_QUERY, variables, signal);
        return response.notes.noteList;
      },
      mapNode: mapGraphQLNodeToNote,
    },
    [search, entityType, noteType, dateFrom, dateTo, sortBy, sortOrder],
  );
}

/**
 * Total number of notes matching the current filter, used for the header count.
 */
export function useNotesCount(params: NotesListParams) {
  const apiConfig = useApiConfig();

  return useModeAwareQuery<number, Error>(
    ['notes-count', params],
    async ({ signal }) => {
      const response = await graphqlQuery<NoteCountGraphQLResponse>(
        apiConfig,
        NOTE_COUNT_QUERY,
        { filter: buildNotesFilterVariable(params) },
        signal,
      );
      return response.notes.noteCount;
    },
    filterMockNotes(params).length,
    {
      staleTime: 0,
      refetchOnMount: 'always',
    },
  );
}
